import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { dateFmt, formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Tag, RefreshCw, Pencil } from "lucide-react";

const TYPES = [{ v: "feature", l: "Feature" }, { v: "improvement", l: "Improvement" }, { v: "bug", l: "Bug fix" }];
const TYPE_CLS = {
    feature: "bg-emerald-100 text-emerald-800 border-emerald-200",
    bug: "bg-rose-100 text-rose-800 border-rose-200",
    improvement: "bg-sky-100 text-sky-800 border-sky-200",
};

export default function Versions() {
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ version: "", title: "", released_at: "", published: false });
    const [busy, setBusy] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [drafts, setDrafts] = useState({});
    const [itemEdit, setItemEdit] = useState(null);
    const [itemForm, setItemForm] = useState({ type: "feature", title: "", description: "" });
    const [verEdit, setVerEdit] = useState(null);
    const [verForm, setVerForm] = useState({ version: "", title: "", released_at: "" });

    const load = () => api.get("/admin/versions").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);

    const syncCommits = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/versions/sync-commits");
            if (data.added > 0) toast.success(`Imported ${data.added} change${data.added === 1 ? "" : "s"} into a hidden draft — review and tick "Show publicly"`);
            else toast.info(data.message || "No new changes to import");
            load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setSyncing(false); }
    };

    const draft = (vid) => drafts[vid] || { type: "feature", title: "", description: "" };
    const setDraft = (vid, patch) => setDrafts((s) => ({ ...s, [vid]: { ...draft(vid), ...patch } }));

    const createVersion = async () => {
        if (!form.version) { toast.error("Version number is required"); return; }
        setBusy(true);
        try {
            await api.post("/admin/versions", form);
            toast.success("Version created");
            setOpen(false);
            setForm({ version: "", title: "", released_at: "", published: false });
            load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const togglePublished = async (v) => {
        try {
            await api.put(`/admin/versions/${v.id}`, { version: v.version, title: v.title, released_at: v.released_at, published: !v.published });
            load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    const delVersion = async (id) => {
        try { await api.delete(`/admin/versions/${id}`); toast.success("Version deleted"); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    const addItem = async (vid) => {
        const d = draft(vid);
        if (!d.title) { toast.error("Add a title for the item"); return; }
        try {
            await api.post(`/admin/versions/${vid}/items`, { type: d.type, title: d.title, description: d.description });
            setDrafts((s) => ({ ...s, [vid]: { type: "feature", title: "", description: "" } }));
            load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    const toggleVisible = async (vid, it) => {
        try { await api.put(`/admin/versions/${vid}/items/${it.id}`, { visible: !it.visible }); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    const delItem = async (vid, iid) => {
        try { await api.delete(`/admin/versions/${vid}/items/${iid}`); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    const openItemEdit = (vid, it) => { setItemEdit({ vid, id: it.id }); setItemForm({ type: it.type || "feature", title: it.title || "", description: it.description || "" }); };
    const saveItemEdit = async () => {
        if (!itemForm.title.trim()) { toast.error("Title is required"); return; }
        setBusy(true);
        try {
            await api.put(`/admin/versions/${itemEdit.vid}/items/${itemEdit.id}`, { type: itemForm.type, title: itemForm.title, description: itemForm.description });
            toast.success("Item updated"); setItemEdit(null); load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(false); }
    };

    const openVerEdit = (v) => { setVerEdit(v); setVerForm({ version: v.version, title: v.title || "", released_at: (v.released_at || "").slice(0, 10) }); };
    const saveVerEdit = async () => {
        if (!verForm.version.trim()) { toast.error("Version number is required"); return; }
        setBusy(true);
        try {
            await api.put(`/admin/versions/${verEdit.id}`, { version: verForm.version, title: verForm.title, released_at: verForm.released_at, published: verEdit.published });
            toast.success("Version updated"); setVerEdit(null); load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6 pe-fade-up" data-testid="versions-admin">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald flex items-center gap-2">
                        <Tag className="h-6 w-6 text-brand-teal" /> Versioning
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">Manage releases. Tick <b>Show publicly</b> to include an item on the public release notes page.</p>
                </div>
                <div className="flex items-center gap-2">
                <Button variant="outline" disabled={syncing} onClick={syncCommits} className="rounded-lg" data-testid="sync-commits-button">
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" /> Sync from changes</>}
                </Button>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="add-version-button">
                            <Plus className="h-4 w-4 mr-1" /> New Version
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Create a version</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                            <div><Label>Version number</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="e.g. 1.2.0" className="mt-1.5 font-mono" data-testid="version-number-input" /></div>
                            <div><Label>Title (optional)</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Matching improvements" className="mt-1.5" data-testid="version-title-input" /></div>
                            <div><Label>Release date</Label><Input type="date" value={form.released_at} onChange={(e) => setForm({ ...form, released_at: e.target.value })} className="mt-1.5" data-testid="version-date-input" /></div>
                            <div className="flex items-center justify-between rounded-lg border border-border p-3">
                                <div><div className="text-sm font-medium">Published</div><div className="text-xs text-muted-foreground">Show this version on the public page</div></div>
                                <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="version-published-switch" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button disabled={busy} onClick={createVersion} className="bg-brand-emerald hover:bg-brand-emeraldhover" data-testid="save-version-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {rows === null ? (
                <div className="grid place-items-center py-16 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : rows.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground bg-white border border-border rounded-xl">No versions yet. Create one to get started.</div>
            ) : (
                <div className="space-y-5">
                    {rows.map((v) => (
                        <div key={v.id} className="bg-white border border-border rounded-xl overflow-hidden" data-testid={`version-card-${v.version}`}>
                            <div className="px-5 py-4 border-b border-border flex flex-wrap items-center gap-3 bg-secondary/40">
                                <span className="font-mono text-lg font-bold text-brand-emerald">v{v.version}</span>
                                {v.title && <span className="text-brand-emerald font-medium">{v.title}</span>}
                                <span className="text-sm text-muted-foreground">{dateFmt(v.released_at)}</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${v.published ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`version-status-${v.version}`}>{v.published ? "Released" : "Draft"}</span>
                                <div className="ml-auto flex items-center gap-3">
                                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                        Published
                                        <Switch checked={v.published} onCheckedChange={() => togglePublished(v)} data-testid={`version-publish-toggle-${v.version}`} />
                                    </label>
                                    <Button variant="ghost" size="icon" onClick={() => openVerEdit(v)} data-testid={`edit-version-${v.version}`}><Pencil className="h-4 w-4" /></Button>
                                    <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => delVersion(v.id)} data-testid={`delete-version-${v.version}`}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>

                            <div className="p-4 space-y-2">
                                {(v.items || []).length === 0 && <div className="text-sm text-muted-foreground py-2">No items yet.</div>}
                                {(v.items || []).map((it) => (
                                    <div key={it.id} className="flex items-start gap-3 border border-border rounded-lg p-3" data-testid={`version-item-${it.id}`}>
                                        <label className="flex items-center gap-2 shrink-0 pt-0.5">
                                            <Checkbox checked={it.visible} onCheckedChange={() => toggleVisible(v.id, it)} data-testid={`item-visible-${it.id}`} />
                                            <span className="text-[11px] text-muted-foreground">Show publicly</span>
                                        </label>
                                        <span className={`shrink-0 inline-flex items-center h-6 px-2 rounded-full border text-[11px] font-semibold capitalize ${TYPE_CLS[it.type] || TYPE_CLS.feature}`}>{it.type}</span>
                                        <div className="min-w-0 flex-1">
                                            <div className={`text-sm font-medium ${it.visible ? "text-brand-emerald" : "text-muted-foreground line-through"}`}>{it.title}</div>
                                            {it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button variant="ghost" size="icon" onClick={() => openItemEdit(v.id, it)} data-testid={`edit-item-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                                            <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => delItem(v.id, it.id)} data-testid={`delete-item-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                    </div>
                                ))}

                                <div className="grid sm:grid-cols-12 gap-2 items-start pt-2 border-t border-border mt-2">
                                    <div className="sm:col-span-3">
                                        <Select value={draft(v.id).type} onValueChange={(val) => setDraft(v.id, { type: val })}>
                                            <SelectTrigger className="h-9" data-testid={`item-type-${v.version}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <Input value={draft(v.id).title} onChange={(e) => setDraft(v.id, { title: e.target.value })} placeholder="Item title" className="sm:col-span-4 h-9" data-testid={`item-title-${v.version}`} />
                                    <Input value={draft(v.id).description} onChange={(e) => setDraft(v.id, { description: e.target.value })} placeholder="Short description (optional)" className="sm:col-span-4 h-9" data-testid={`item-desc-${v.version}`} />
                                    <Button variant="outline" className="sm:col-span-1 h-9" onClick={() => addItem(v.id)} data-testid={`add-item-${v.version}`}><Plus className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!itemEdit} onOpenChange={(o) => !o && setItemEdit(null)}>
                <DialogContent data-testid="edit-item-dialog">
                    <DialogHeader><DialogTitle>Edit item</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Type</Label>
                            <Select value={itemForm.type} onValueChange={(val) => setItemForm({ ...itemForm, type: val })}>
                                <SelectTrigger className="mt-1.5" data-testid="edit-item-type"><SelectValue /></SelectTrigger>
                                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Title</Label><Input value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} className="mt-1.5" data-testid="edit-item-title" /></div>
                        <div><Label>Description</Label><Textarea value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} className="mt-1.5" rows={3} data-testid="edit-item-desc" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setItemEdit(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={saveItemEdit} className="bg-brand-emerald hover:bg-brand-emeraldhover" data-testid="save-item-edit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!verEdit} onOpenChange={(o) => !o && setVerEdit(null)}>
                <DialogContent data-testid="edit-version-dialog">
                    <DialogHeader><DialogTitle>Edit version</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>Version number</Label><Input value={verForm.version} onChange={(e) => setVerForm({ ...verForm, version: e.target.value })} className="mt-1.5 font-mono" data-testid="edit-version-number" /></div>
                        <div><Label>Title</Label><Input value={verForm.title} onChange={(e) => setVerForm({ ...verForm, title: e.target.value })} className="mt-1.5" data-testid="edit-version-title" /></div>
                        <div><Label>Release date</Label><Input type="date" value={verForm.released_at} onChange={(e) => setVerForm({ ...verForm, released_at: e.target.value })} className="mt-1.5" data-testid="edit-version-date" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVerEdit(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={saveVerEdit} className="bg-brand-emerald hover:bg-brand-emeraldhover" data-testid="save-version-edit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
